import { UserType } from '@/data/constants';

/**
 * Single source of truth for what each account type is allowed to see and do.
 * Imported by the router guards, the header navigation and every dashboard so
 * a role can never "leak" contractor tooling into a homeowner account.
 */

export type Role = UserType;

/**
 * COVER IMAGES moved to src/lib/coverImage.ts (upload -> trade library ->
 * branded marketplace cover). Re-exported here so the existing call sites
 * (JobCard, OwnerDashboard, AdminModeration) keep one import path and there is
 * still exactly ONE implementation.
 */
export { coverImageFor, coverUrlFor, PROJECT_PLACEHOLDER, coverCoverageReport } from '@/lib/coverImage';
export type { ProjectCover, CoverSource } from '@/lib/coverImage';


export const isHomeowner = (role?: string | null) => role === 'homeowner';
export const isGc = (role?: string | null) => role === 'gc';
export const isSub = (role?: string | null) => role === 'subcontractor';

/** Only contractor-side accounts may buy / unlock leads or hold a membership. */
export const canBuyLeads = (role?: string | null) => role === 'subcontractor' || role === 'gc';
export const canHoldSubscription = (role?: string | null) => canBuyLeads(role);

/** Homeowners post projects; GCs post work orders; subs do neither. */
export const canPostProjects = (role?: string | null) => role === 'homeowner';
export const canPostWorkOrders = (role?: string | null) => role === 'gc';

/** Who is allowed to browse the lead marketplace feed. */
export const canBrowseMarketplace = (role?: string | null) => canBuyLeads(role);

export interface RoleCapabilities {
  postProjects: boolean;
  postWorkOrders: boolean;
  browseMarketplace: boolean;
  buyLeads: boolean;
  subscriptions: boolean;
  leadBalance: boolean;
}

export const capabilitiesFor = (role?: string | null): RoleCapabilities => ({
  postProjects: canPostProjects(role),
  postWorkOrders: canPostWorkOrders(role),
  browseMarketplace: canBrowseMarketplace(role),
  buyLeads: canBuyLeads(role),
  subscriptions: canHoldSubscription(role),
  leadBalance: canBuyLeads(role),
});

/**
 * STRICT role parser: returns null when the value is missing or unrecognised.
 *
 * Use this anywhere a WRONG answer is worse than no answer — in particular
 * when a profile row is about to be created. mkt_profiles_guard freezes
 * user_type after insert, so a guessed role becomes permanent: that is what
 * turned a painting contractor's account into a homeowner account and hid the
 * whole find-jobs side of the product from them.
 */
export const parseRole = (value?: string | null): Role | null => {
  const v = (value || '').trim().toLowerCase();
  if (v === 'homeowner' || v === 'owner' || v === 'home_owner') return 'homeowner';
  if (v === 'gc' || v === 'general_contractor' || v === 'contractor') return 'gc';
  if (v === 'subcontractor' || v === 'sub') return 'subcontractor';
  return null;
};

/**
 * Lenient parser kept for call sites that MUST end up with a role (the
 * registration form always supplies one explicitly). Never use it to guess a
 * role for a profile row that does not exist yet — use parseRole instead.
 */
export const normalizeRole = (value?: string | null): Role => parseRole(value) ?? 'homeowner';

